import { type DocumentNode } from '@apollo/client';
import {
  BrandLelandLogoSilhouette,
  Button,
  ButtonColor,
  ButtonWidth,
  formatName,
  ProfileImage,
  Tag,
} from '@leland-dev/leland-ui-library';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { type FC, type ReactNode, useState } from 'react';

import {
  type ApplicantInput,
  ReferralType,
} from '../../__generated-gql-types__/globalTypes';
import { useAuth, useLogout, useSignup } from '../../context/AuthContext';
import { LexSlug, useLex } from '../../context/LexContext';
import { getFirstErrorMessage } from '../../utils/apollo';
import { SITE_URL } from '../../utils/constants';
import { useReferralParameters } from '../../utils/hooks/useReferralParameters';
import { getReferralTypeFromString } from '../../utils/referrals';
import { getUrlObject } from '../../utils/url';
import { AuthCard } from '../auth/AuthCard';

import { ErrorMessage } from './Inputs';
import { useSignupFormBasicUserByReferralCodeQuery } from './__generated-gql-types__/SignupForm.generated';
type ModalProps =
  | {
      isModal: true;
      setMode: React.Dispatch<React.SetStateAction<'login' | 'signup'>>;
    }
  | {
      isModal?: false;
      setMode?: never;
    };

type SignupFormV2Props = ModalProps & {
  className?: string;
  onApplicantModalLogin?: () => void;
  onCoachModalLogin?: () => void;
  redeemCode?: string;
  refetchQueries?: DocumentNode[];
  title?: ReactNode;
  subtitle?: ReactNode;
};

export const SignupForm: FC<SignupFormV2Props> = ({
  className = '',
  isModal,
  setMode,
  onApplicantModalLogin,
  onCoachModalLogin,
  refetchQueries,
  redeemCode,
  title,
  subtitle,
}) => {
  const router = useRouter();
  const { currentUser } = useAuth();
  const { signupAsApplicant, signupAsApplicantLoading } = useSignup();
  const { logout } = useLogout();

  const { isLexEnabled } = useLex();
  const isCustomerReferralLexEnabled = isLexEnabled(
    LexSlug.FEATURE_CUSTOMER_REFERRAL,
  );

  const { referralCode, referralType } = useReferralParameters();

  const [error, setError] = useState<Nullable<string>>(null);

  const refType = getReferralTypeFromString(referralType);
  const { data, loading: loadingReferrerInfo } =
    useSignupFormBasicUserByReferralCodeQuery({
      variables: {
        referralCode: referralCode ?? '',
      },
      skip: !referralCode || refType !== ReferralType.CUSTOMER_REFER_CUSTOMER,
    });

  const referrerName = data?.basicUserByReferralCode?.firstName;
  const referrerPictureLink = data?.basicUserByReferralCode?.pictureLink;

  const handleSignUpAsApplicant = async () => {
    try {
      await signupAsApplicant({
        applicant: {} as ApplicantInput,
      });

      if (onApplicantModalLogin) {
        onApplicantModalLogin?.();
      } else {
        await router.push(getUrlObject('/apply', router.query));
      }
    } catch (e) {
      setError(
        (Array.isArray(e) ? getFirstErrorMessage(e) : null) ??
          // Most likely 4xx server error that is difficult to act on. Returning default error message
          'We ran into an error while signing you up. Please check your verification code and try again.',
      );
    }
  };

  return (
    <div className={`${className}`}>
      {currentUser && !currentUser.applicant ? (
        <>
          {title ?? (
            <h1 className="mb-4 w-full text-center text-4xl font-medium">
              Join Leland as a Customer
            </h1>
          )}
          {subtitle ?? (
            <p className="mb-4 w-full text-center">
              Access the world&rsquo;s top expertise on Leland! Coach accounts
              can sign up as customers and easily switch between the two modes.
            </p>
          )}
          <div className="mt-4 w-full">
            <Button
              label={
                signupAsApplicantLoading
                  ? 'Creating...'
                  : 'Create customer account'
              }
              onClick={handleSignUpAsApplicant}
              disabled={signupAsApplicantLoading}
              buttonColor={ButtonColor.PRIMARY}
              width={ButtonWidth.FULL}
              id="signup_form:create_customer_account"
            />
          </div>
          {error ? <ErrorMessage errorMessage={error} /> : null}
          <ProfileImage
            className="mx-auto mb-2 mt-5 size-8 text-center"
            pictureLink={currentUser.pictureLink}
            firstName={currentUser.firstName}
            lastName={currentUser.lastName}
          />
          <p className="mb-2 w-full text-center leading-4 text-gray-600">
            Currently logged in as{' '}
            {formatName({
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
            })}
          </p>
          {isModal ? null : (
            <button
              className="flex place-self-center text-gray-400 underline"
              onClick={async (e) => {
                e.preventDefault();
                await logout();
              }}
              id="signup_form:logout"
            >
              Log Out
            </button>
          )}
        </>
      ) : (
        <>
          {isModal ? (
            <div className="flex flex-col place-items-center">
              {title ?? (
                <h1 className="mb-2 w-full text-center text-2xl font-medium">
                  Join Leland
                </h1>
              )}
              {subtitle ?? (
                <p className="mb-8 w-4/5 text-center leading-4 text-gray-500">
                  A dedicated mentor can make all the difference. Find a coach
                  to help you achieve your goals.
                </p>
              )}
            </div>
          ) : (
            <div className="mb-8 space-y-2.5">
              {redeemCode ? (
                <BrandLelandLogoSilhouette className="mx-auto hidden size-32 justify-center text-leland-primary lg:block" />
              ) : null}
              <h1 className="w-full text-center text-4xl font-medium">
                {redeemCode
                  ? 'Create an account to unlock your free month of coaching.'
                  : refType === ReferralType.CUSTOMER_REFER_CUSTOMER &&
                      isCustomerReferralLexEnabled
                    ? 'Create an account to claim your $50 Leland credit'
                    : 'Create your Leland account'}
              </h1>
              {isCustomerReferralLexEnabled &&
              refType === ReferralType.CUSTOMER_REFER_CUSTOMER ? (
                loadingReferrerInfo ? (
                  <div className="text-center">
                    <Tag text="Loading..." />
                  </div>
                ) : referrerPictureLink ? (
                  <div className="text-center">
                    <Tag
                      CustomLeftIcon={() => (
                        <Image
                          src={referrerPictureLink}
                          alt="Referred by"
                          width={20}
                          height={20}
                          className="size-6 rounded-full"
                        />
                      )}
                      text={`Referred by ${referrerName}`}
                    />
                  </div>
                ) : null
              ) : null}
              <p className=" w-full text-center leading-4 text-gray-500">
                By continuing below, you agree to Leland’s{' '}
                <a
                  className="link text-leland-primary"
                  href={`${SITE_URL}/legal/terms`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  id="signup_form:view_terms"
                >
                  terms
                </a>{' '}
                and{' '}
                <a
                  className="link text-leland-primary"
                  href={`${SITE_URL}/legal/privacy-policy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  id="signup_form:view_privacy_policy"
                >
                  privacy policy
                </a>
                .
              </p>
            </div>
          )}
          <div className="mb-5 grid w-full gap-6">
            <AuthCard
              screenHint="signup"
              isModal={isModal}
              onApplicantModalLogin={onApplicantModalLogin}
              onCoachModalLogin={onCoachModalLogin}
              refetchQueries={refetchQueries}
              redeemCode={redeemCode}
              referralCode={referralCode}
              referralType={referralType}
            />
          </div>
          <p className="w-full text-center text-leland-gray-light">
            Already have an account?{' '}
            {isModal ? (
              <button
                className="link link--primary font-medium"
                onClick={() => setMode('login')}
                id="signup_form_modal:login_button"
              >
                Log in
              </button>
            ) : (
              <Link
                href={getUrlObject('/login', router.query)}
                className="link link--primary"
                id="signup_form:login_button"
              >
                Log in
              </Link>
            )}
          </p>
          <div className="mt-4 w-full text-center text-leland-gray-extra-light">
            <a
              className="link px-2"
              href={`${SITE_URL}/legal/terms`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Terms & Conditions
            </a>
            ·
            <a
              className="link px-2"
              href={`${SITE_URL}/legal/privacy-policy`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Privacy Policy
            </a>
          </div>
        </>
      )}
    </div>
  );
};
